import { useEffect, useRef, useState, type ReactNode } from 'react'
import { IconaCopia } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'
import '../../stile-posta-caselle.css'

/**
 * Mittente e destinatari di un messaggio aperto, selezionabili e con «Copia» accanto a ciascuno (richiesta
 * di Davide del 2/11/2026: «non si può copiare la mail del mittente o destinatario»). Stavano dentro il
 * pulsante che apre e chiude il messaggio, e il testo di un pulsante non si seleziona: qui sono testo
 * normale, fuori dal pulsante. Li usano la conversazione (Posta.tsx) e l'archivio delle caselle.
 *
 * «Copia» copia il solo indirizzo, senza il nome: e' quello che si incolla in un campo «A» o in una ricerca.
 */
export function Indirizzi({ da, a, cc, coda }: {
  da: string; a: string[]; cc: string[]
  /** Quello che va in fondo alla riga del mittente (l'etichetta della provenienza, nell'archivio). */
  coda?: ReactNode
}) {
  return (
    <dl className="posta-indirizzi">
      <dt>Da</dt>
      <dd>
        <Indirizzo voce={da} />
        {coda}
      </dd>
      <dt>A</dt>
      <dd>{a.length ? a.map((x, i) => <Indirizzo key={`${i}-${x}`} voce={x} />) : '—'}</dd>
      {cc.length > 0 && (
        <>
          <dt>Cc</dt>
          <dd>{cc.map((x, i) => <Indirizzo key={`${i}-${x}`} voce={x} />)}</dd>
        </>
      )}
    </dl>
  )
}

function Indirizzo({ voce }: { voce: string }) {
  return (
    <span className="posta-indirizzo">
      <span className="posta-indirizzo-testo">{posta.nomeEIndirizzo(voce)}</span>
      <BottoneCopia testo={posta.soloIndirizzo(voce)} />
    </span>
  )
}

/** Il comando «Copia», con «Copiato» per due secondi. Fuori da https il browser puo' negarlo: lo dice. */
export function BottoneCopia({ testo }: { testo: string }) {
  const [esito, setEsito] = useState<'copiato' | 'negato' | null>(null)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  async function copia() {
    let riuscito = false
    try {
      await navigator.clipboard.writeText(testo)
      riuscito = true
    } catch {
      riuscito = copiaDiRiserva(testo)
    }
    setEsito(riuscito ? 'copiato' : 'negato')
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setEsito(null), 2000)
  }

  return (
    <button type="button" className="posta-copia" onClick={() => void copia()}
            aria-label={`Copia ${testo}`} title={`Copia ${testo}`}>
      <IconaCopia />
      <span role="status">{esito === 'copiato' ? 'Copiato' : esito === 'negato' ? 'Non copiato' : 'Copia'}</span>
    </button>
  )
}

/** Senza l'API degli appunti (pagina non sicura, permesso negato): il vecchio comando di copia. */
function copiaDiRiserva(testo: string): boolean {
  const campo = document.createElement('textarea')
  campo.value = testo
  campo.setAttribute('readonly', '')
  campo.style.position = 'fixed'
  campo.style.opacity = '0'
  document.body.appendChild(campo)
  campo.select()
  try { return document.execCommand('copy') } catch { return false } finally { campo.remove() }
}
